// ================================================
// PLACEHOLDER MATH ONLY - NOT REAL LANDED COST
// This will be deleted and replaced by the Odoo
// pricing engine. Do not extend or rely on this.
// ================================================

import type {
  Material,
  PricingContext,
  FxSettings,
  DutyRate,
} from "@/types";
import { stockStateOf } from "@/services/stock";

// ---- utilities -------------------------------------------------------------

function round2(n: number): number { return Math.round(n * 100) / 100; }
function sum(arr: number[]): number { return arr.reduce((a, b) => a + b, 0); }

export function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

export function clampQty(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.floor(n));
}

// ---- brand-lock validation -------------------------------------------------

const REQUIRED_OEM: Record<string, string> = {
  copper: "CommScope",
  fiber:  "Corning",
};

export function requiredOemFor(category: string): string | null {
  return REQUIRED_OEM[category] ?? null;
}

export function isBrandViolation(row: Material): boolean {
  const req = requiredOemFor(row.category);
  return req !== null && row.oem !== req;
}

export function brandViolationTip(row: Material): string {
  const req = requiredOemFor(row.category);
  if (!req) return "";
  const label = row.category === "copper" ? "Copper" : "Fiber";
  return `Brand locked. This client requires ${req} for ${label}.`;
}

export interface WarningsSummary {
  brandViolations: number;
  inStock: number;
  // The sibling that makes inStock readable. Without it a 0 in that field is
  // two different sentences -- "no line has stock" and "no line has been
  // checked" -- and the strip stated the first while meaning the second on
  // every reopened quote.
  stockUnknown: number;
}

export function computeWarningsSummary(rows: Material[]): WarningsSummary {
  let brandViolations = 0;
  let inStock = 0;
  let stockUnknown = 0;
  for (const r of rows) {
    if (isBrandViolation(r)) brandViolations++;
    // ⚠️ COUNTS ONLY LINES KNOWN TO BE IN STOCK. Unknown and a stated 0 are
    // both excluded, for different reasons: one is no evidence, the other is
    // evidence of none. Neither is "in stock", which is all inStock claims
    // -- but only one of them is a gap in what we know, which is what
    // stockUnknown reports separately so the strip can say which 0 it means.
    //
    // Via stockStateOf rather than an inlined `r.stock != null && r.stock > 0`:
    // that test was correct, but it was a second copy of the rule the pills
    // and both filters already share, and a second copy is how the collapse
    // slice 2.0b-ii removed creeps back in.
    const state = stockStateOf(r.stock);
    if (state === "in") inStock++;
    else if (state === "unknown") stockUnknown++;
  }
  return { brandViolations, inStock, stockUnknown };
}

export function computeEffectiveRate(fx: FxSettings): number {
  return round2(fx.customsRate * (1 + fx.riskBufferPct / 100));
}

// ---- main pipeline ---------------------------------------------------------

// (recalcRow lived here: a single-row recalc with hard-coded netPrice x 0.06
// freight and x 0.11 duty, left over from an earlier MaterialTable. It had
// ZERO callers and its ratios were unrelated to anything the app or the engine
// computes, so it was one more set of plausible-looking numbers waiting to be
// wired up by mistake. Deleted rather than left "in case".)

// The remaining derived fields. netPrice is the one that matters.
//
// ⚠️ netPrice IS WHY THIS FUNCTION STILL EXISTS. It is the single output of
// this file that reaches the backend: materialToBackendLine sends it as
// `unit_price`, so every engine price on the quote screen is priced from what
// this computes. Deleting this function stops the engine being asked to price
// anything at all.
//
// WHAT USED TO BE HERE. This took a PricingContext and a PricingMode and, on
// the import branch, allocated a shipment's cost across the lines and charged
// BCD + SWS on an FX-adjusted assessable value -- deciding "is this imported?"
// from the line's ORIGIN string, which every catalog product carries. The
// blob shipments that fed it are gone (BF5d-3): a quote's real freight is
// allocated by the engine, per line, and arrives as freight_pct. Neither the
// context nor the mode is read here any more, so neither is a parameter.
//
// landedCost, sellPrice and lineTotal are what is left of the placeholder
// chain. Nothing on the grid renders them -- Sell Price and Line Total are
// live columns that read the engine and dash without it -- and nothing sends
// them. They survive because SidePanel's Sourcing Split and its IMPORT margin
// summary still aggregate them, and for no other reason.
// ⚠️ A LINE WITH NO LIST PRICE PRODUCES NO netPrice. THIS IS THE WHOLE
// UNPRICED-LINE MECHANISM AND IT MUST NOT BE "SIMPLIFIED".
//
// A product off the real master arrives with no price -- anixify.product has
// no price column, by design, because every price comes from the engine. The
// tempting shortcut is `(r.listPrice ?? 0)`, and it is the bug:
//
//   listPrice 0 -> netPrice 0 -> unit_price 0 reaches the engine -> the
//   engine answers with a COMPLETE AND VALID result of zero -> the live
//   columns render "Rs 0.00" beside a confident "28.0%" GP, under the green
//   `live` badge that means "the validated engine computed this".
//
// The live/est dash cannot catch that. It is keyed on `typeof liveValue ===
// "number"`, i.e. on the BACKEND RESULT being absent -- and here the result
// is present and numerically fine. Confirmed against the deployed bundle.
//
// The other shortcut is to let `undefined * x` through: that yields NaN,
// JSON.stringify turns NaN into null, and the bridge's _to_decimal rejects
// None loudly -- which fails the WHOLE batch, so one priceless product blanks
// every other line's engine numbers and reports it in engine internals.
//
// So neither number is manufactured. netPrice stays undefined, and
// services/unpricedGuard.ts keeps such a line out of the pricing request
// entirely. Typing a List Price in the grid is what clears it.
//
// landedCost/sellPrice/lineTotal are set to 0 rather than left undefined
// because they are placeholder aggregates that SidePanel SUMS -- nothing on
// the grid renders them (Sell Price and Line Total are live columns). An
// undefined in a running total poisons it to NaN, whereas 0 is the honest
// answer to "what does a line with no price yet contribute to an estimate".
// They are not sent anywhere and they are not what makes a line unpriced;
// the absent netPrice is.
export function recomputeAll(rows: Material[]): Material[] {
  return rows.map((r) => {
    if (r.listPrice === undefined || r.listPrice === null) {
      // ⚠️ A STORED NET PRICE IS KEPT, NOT BLANKED. THIS BRANCH ADDS NO
      // FORMULA -- IT DECLINES TO DERIVE A VALUE THE ROW ALREADY HOLDS.
      //
      // A line reopened from a save before the list/discount split was
      // recorded has a REAL netPrice (the backend's unit_price, what the
      // engine priced from) and no list price to have derived it from.
      // Falling through to the blanking branch below would destroy it:
      // netPrice undefined makes the line unpriced, which drops it out of
      // the pricing request, BLOCKS THE SAVE, and moves pricingSignatureOf
      // so every stored engine number is discarded as stale. On every quote
      // saved before addon 17.0.1.17.0 -- which is all of them.
      //
      // The old code got away with it only because savedQuoteToMaterials
      // fabricated `listPrice = unit_price, discountPct = 0` to reproduce
      // netPrice through the derive below. That fabrication is what made a
      // reopened quote claim 0% off an already-discounted price, so it is
      // gone -- and this branch is what replaces it honestly.
      //
      // NOT AN EXTENSION OF THIS FILE'S PLACEHOLDER MATH: no new arithmetic
      // is introduced, and the aggregates below are the same ones already
      // computed from netPrice everywhere else.
      if (r.netPrice !== undefined && r.netPrice !== null) {
        const landedCost = r.netPrice;
        const sellPrice = round2(landedCost / (1 - r.marginPct / 100));
        return {
          ...r,
          landedCost,
          sellPrice,
          lineTotal: round2(sellPrice * r.qty),
        };
      }
      return { ...r, netPrice: undefined, landedCost: 0, sellPrice: 0, lineTotal: 0 };
    }
    // `?? 0` because discountPct is optional now. Reachable only WITH a list
    // price, i.e. on a line whose split is stated -- a fresh line always
    // carries a percentage (rule-derived or 0), and a reconciled one is
    // written with both halves at once. It is a guard, not a default anyone
    // should rely on: an undefined here would otherwise make the whole
    // expression NaN, since this project compiles with strictNullChecks off.
    const netPrice = round2(r.listPrice * (1 - (r.discountPct ?? 0) / 100));
    const landedCost = netPrice;
    const sellPrice = round2(landedCost / (1 - r.marginPct / 100));
    const lineTotal = round2(sellPrice * r.qty);
    return { ...r, netPrice, landedCost, sellPrice, lineTotal };
  });
}

// ---- panel aggregates ------------------------------------------------------

export const ORIGINS: readonly string[] = ["US Factory", "China", "Goa Plant", "Local Stock"];
export const ORIGIN_COLOURS: Record<string, string> = {
  "US Factory":  "#4F46E5",
  "China":       "#0891B2",
  "Goa Plant":   "#059669",
  "Local Stock": "#A1A1AA",
};

export interface SourcingSplitRow {
  origin: string;
  lines: number;
  value: number;
  pct: number;
  colour: string;
}

export function computeSourcingSplit(rows: Material[]): SourcingSplitRow[] {
  const totalValue = sum(rows.map((r) => r.lineTotal));
  return ORIGINS.map((origin) => {
    const rs = rows.filter((r) => r.origin === origin);
    const value = sum(rs.map((r) => r.lineTotal));
    const pct = totalValue > 0 ? (value / totalValue) * 100 : 0;
    return { origin, lines: rs.length, value, pct, colour: ORIGIN_COLOURS[origin] };
  });
}

export interface MarginSummary {
  totalLanded: number;
  totalValue: number;
  grossMargin: number;
  negotiationBuffer: number;
  finalQuote: number;
  realisedMarginPct: number;
}

export function computeMarginSummary(rows: Material[], ctx: PricingContext): MarginSummary {
  const totalLanded = sum(rows.map((r) => r.landedCost * r.qty));
  const totalValue = sum(rows.map((r) => r.lineTotal));
  const grossMargin = totalValue - totalLanded;
  const finalQuote = totalValue - ctx.negotiationBuffer;
  const realisedMarginPct = finalQuote > 0 ? ((finalQuote - totalLanded) / finalQuote) * 100 : 0;
  return {
    totalLanded, totalValue, grossMargin,
    negotiationBuffer: ctx.negotiationBuffer,
    finalQuote, realisedMarginPct,
  };
}

// THE MARGIN SUMMARY BUILT FROM ENGINE NUMBERS, for the domestic panel.
//
// ⚠️ THIS IS AGGREGATION, NOT PRICING, and that distinction is what lets it
// live in this otherwise-frozen file. It sums values the validated engine
// already produced -- extended_sp per line, and cost_with_sv x qty -- exactly
// as QuotePdfPreview sums lineTotal for its subtotal. Nothing here derives a
// price, applies a margin, or re-rounds an engine result.
//
// WHY IT EXISTS. computeMarginSummary above reads landedCost and lineTotal off
// the rows, both placeholder-derived. Once the domestic branch stopped
// inflating landedCost, the placeholder totals stopped being merely wrong and
// started being wrong in a NEW way: totalValue is a placeholder sell price
// while totalLanded is a real net price, so grossMargin could read large and
// NEGATIVE on a perfectly healthy quote. Zeroing the placeholder would have
// produced that. Reading the engine instead produces the truth.
//
// RETURNS NULL WHEN THE QUOTE IS NOT PRICED, rather than zeros. A panel of
// zeros claims a quote worth nothing; null lets the caller say "not priced
// yet", which is what is actually true.
export function computeEngineMarginSummary(
  rows: Material[],
  ctx: PricingContext,
  priced: Map<number, { unit_sp?: number; extended_sp?: number; cost_with_sv?: number }>,
): MarginSummary | null {
  if (rows.length === 0) return null;

  let totalValue = 0;
  let totalLanded = 0;
  for (const r of rows) {
    const p = priced.get(r.lineNo);
    // EVERY line must be priced, or the total is a blend of engine numbers and
    // silence -- the same rule the table's own footer applies before showing a
    // live total.
    if (!p || typeof p.extended_sp !== "number" || typeof p.cost_with_sv !== "number") {
      return null;
    }
    totalValue += p.extended_sp;
    totalLanded += p.cost_with_sv * r.qty;
  }

  const grossMargin = totalValue - totalLanded;
  const finalQuote = totalValue - ctx.negotiationBuffer;
  const realisedMarginPct =
    finalQuote > 0 ? ((finalQuote - totalLanded) / finalQuote) * 100 : 0;
  return {
    totalLanded, totalValue, grossMargin,
    negotiationBuffer: ctx.negotiationBuffer,
    finalQuote, realisedMarginPct,
  };
}

// ---- duty rates (display helpers for the Sourcing > Duty Rates panel) -----
//
// PLACEHOLDER MATH ONLY, same as the rest of this file — not real landed
// cost, will be deleted and replaced by the Odoo pricing engine. Do not
// extend or rely on this. This is a display helper only: it feeds the
// small live-calculated preview line in DutyRateDetailPanel, nothing else.
// SWS is a % of BCD, not of goods value — same convention as SWS_RATE above.

const DUTY_PREVIEW_GOODS_VALUE = 100_000; // Rs 1,00,000 — the fixed example value shown in the panel

export function computeDutyEffectiveTotalPct(rate: Pick<DutyRate, "bcdPct" | "swsPct">): number {
  // Combined BCD + SWS as a share of goods value. IGST excluded — it's recoverable.
  return round2(rate.bcdPct * (1 + rate.swsPct / 100));
}

export interface DutyPreview {
  bcd: number;
  sws: number;
  igst: number;
  landedDutyCost: number; // BCD + SWS only — IGST excluded, it's recoverable
}

export function computeDutyPreview(rate: Pick<DutyRate, "bcdPct" | "swsPct" | "igstPct">): DutyPreview {
  const bcd = round2(DUTY_PREVIEW_GOODS_VALUE * (rate.bcdPct / 100));
  const sws = round2(bcd * (rate.swsPct / 100));
  const igst = round2((DUTY_PREVIEW_GOODS_VALUE + bcd + sws) * (rate.igstPct / 100));
  const landedDutyCost = round2(bcd + sws);
  return { bcd, sws, igst, landedDutyCost };
}
